import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Terminal, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/api";
import { LogoMark } from "../../components/Logo";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(form.email, form.password);
      toast.success("ACCESS GRANTED");
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-5" data-testid="admin-login-page">
      <div className="terminal-grid absolute inset-0 opacity-50" />
      <div className="absolute left-1/2 top-1/3 h-80 w-80 -translate-x-1/2 rounded-full bg-signal/10 blur-[130px]" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md border border-line bg-surface/90 backdrop-blur-xl"
      >
        <div className="flex items-center justify-between border-b border-line px-6 py-3.5">
          <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted">
            <Terminal size={12} className="text-signal" /> FTC-ADMIN / SECURE SHELL
          </span>
          <span className="flex gap-1.5">
            <i className="h-2 w-2 rounded-full bg-loss/70" />
            <i className="h-2 w-2 rounded-full bg-[#F59E0B]/70" />
            <i className="h-2 w-2 rounded-full bg-signal/70" />
          </span>
        </div>
        <form onSubmit={submit} className="p-8">
          <div className="flex justify-center"><LogoMark size={44} /></div>
          <h1 className="mt-5 text-center font-display text-2xl font-800 tracking-tight text-white">Desk Access</h1>
          <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-muted">Authorized personnel only</p>
          {error && (
            <p className="mt-6 border border-loss/50 bg-loss/10 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.1em] text-loss" data-testid="login-error">
              {error}
            </p>
          )}
          <div className="mt-7 space-y-5">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Email</label>
              <input
                type="email" required autoComplete="email"
                data-testid="login-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 w-full border border-line bg-ink px-3.5 py-3 font-mono text-sm text-white outline-none transition-colors focus:border-signal"
                placeholder="admin@terminaledge.io"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Password</label>
              <input
                type="password" required autoComplete="current-password"
                data-testid="login-password-input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-2 w-full border border-line bg-ink px-3.5 py-3 font-mono text-sm text-white outline-none transition-colors focus:border-signal"
                placeholder="••••••••••••"
              />
            </div>
          </div>
          <button
            type="submit" disabled={busy}
            data-testid="login-submit-btn"
            className="btn-beam btn-gradient mt-8 flex w-full items-center justify-center gap-2 px-5 py-3.5 font-display text-sm font-800 text-white transition-opacity disabled:opacity-50"
          >
            <LogIn size={13} /> {busy ? "Authenticating…" : "Open Terminal"}
          </button>
          <Link to="/" data-testid="login-back-home" className="mt-6 block text-center font-mono text-[10px] uppercase tracking-[0.2em] text-muted transition-colors hover:text-signal">
            ← Back to public terminal
          </Link>
        </form>
      </motion.div>
    </div>
  );
}
